import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useMediaQuery } from "react-responsive";
import FormField from "Components/Fields/FormField";
import Loading from "Components/Loading";
import { Routes } from "Utils/Constants";
import { Regexes } from "Utils/Regexes";
import { useHistory } from "react-router-dom";
import { clickifyPhoneNumber } from "Utils/Utils";

import useVipAccountCache from "Utils/Hooks/useVipAccountCached";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useSession from "Utils/Hooks/useSession";

import checkDouble from "Assets/Icons/check-double.svg";
import warning from "Assets/Icons/warning.svg";

import "./ManageLimit.scss";
//import useCacheData from "Components/useCacheData";

const ManageLimit = () => {
  const history = useHistory();
  const { vipAccount, setVipAccount } = useVipAccountCache();
  //const vipAccount = useCacheData();
  const { session } = useSession();
  const mode = useMode();
  const vipApi = useVipApiFetcher();

  const limitAmount = vipAccount.limitAmount;
  const formattedLimitAmount = limitAmount?.toLocaleString();
  const [limitRequestAmount, setLimitRequestAmount] = useState(
    vipAccount.limitAmount,
  );
  const [limitRequestValid, setLimitRequestValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [limit, setLimit] = useState(formattedLimitAmount?.split(".")[0]);
  const isMobile = useMediaQuery({
    query: "(max-width: 767.98px)",
  });

  const depositMode = mode !== MODE.withdraw;
  const transactionRoute = depositMode ? Routes.DEPOSIT : Routes.WITHDRAW;
  const nextStep = session.transactionSucceeded
    ? Routes.DASHBOARD
    : transactionRoute;

  // const limitAmount = vipAccount.limitAmount;
  // const formattedLimitAmount = limitAmount?.toLocaleString();
  // const dollars = formattedLimitAmount?.split('.')[0];
  const maxLimitAmount = vipAccount.maxLimitAmount;
  const formattedMaxLimitAmount =
    maxLimitAmount === 0
      ? (limitAmount - 5).toLocaleString()
      : maxLimitAmount?.toLocaleString();

  const proceed =
    limitRequestValid && limitRequestAmount !== vipAccount.limitAmount;

  function onFieldChange(fieldName, fieldValue, isValid) {
    setLimitRequestAmount(fieldValue);
    setLimitRequestValid(isValid);
  }

  const INCREMENTS_OF_5 = {
    errorMessage: `Must be an increment of $5.`,
    action: (amount) => (amount % limitAmount) % 5 == 0,
  };

  const NUMBERS_ONLY = {
    errorMessage: "Whole numbers only.",
    regex: Regexes.NUMBER,
  };

  useEffect(() => {
    vipApi.fetchData("/api/accounts").then((data) => {
      setVipAccount(data);
    });
  }, [successful]);

  const submitForm = (event) => {
    event.preventDefault();
    setSubmitted(true);
    setLoading(true);
    let updatedLimit = Number(limitRequestAmount);
    vipApi
      .fetch("/api/account/limit", "PUT", {
        amount: limitRequestAmount,
      })
      .then((response) => {
        setLoading(false);
        if (response.ok) {
          setSuccessful(true);
          setLimit(updatedLimit.toLocaleString()?.split(".")[0]);
        } else {
          setSuccessful(false);
        }
      })
      .catch((error) => {
        console.error(error);
        setSuccessful(false);
      });
  };

  const refreshButton = (
    <button
      className="link"
      onClick={() => {
        setSubmitted(false);
        setSuccessful(false);
        setLimitRequestAmount(vipAccount.limitAmount);
      }}
    >
      Refresh Form
    </button>
  );

  const success = (
    <div className="success">
      <Row>
        <Col md={4} xs={12}>
          <span className="helper"></span>
          <img src={checkDouble} alt="Checkmark" className="icon" />
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          md={{ span: 8, offset: 0 }}
          className="message"
        >
          <h3>Request Approved</h3>
          <p>
            {clickifyPhoneNumber(
              `Congratulations, your limit change has been approved! Your available limit is now $${limit}. For further assistance please contact CAMS at 1-800-500-1973. `,
            )}{" "}
            {refreshButton}
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={{ span: 10, offset: 1 }} md={{ span: 6, offset: 3 }}>
          <button
            type="button"
            className="submit-button"
            onClick={() => {
              history.push(nextStep);
            }}
          >
            {session.transactionSucceeded
              ? "Done"
              : `Complete ${depositMode ? "Deposit" : "Withdraw"}`}
          </button>
        </Col>
      </Row>
    </div>
  );

  const error = (
    <div className="success">
      <Row>
        <Col md={4} xs={12}>
          <span className="helper"></span>
          <img src={warning} alt="Warning" className="icon" />
        </Col>
        <Col
          xs={{ span: 10, offset: 1 }}
          md={{ span: 8, offset: 0 }}
          className="message"
        >
          <h3>Request Incomplete</h3>
          <p>
            {clickifyPhoneNumber(
              "Your limit change was not approved. For further assistance please contact CAMS at 1-800-500-1973.",
            )}{" "}
            {refreshButton}
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={{ span: 10, offset: 1 }} md={{ span: 6, offset: 3 }}>
          <button
            type="button"
            className="submit-button"
            onClick={() => {
              history.push(nextStep);
            }}
          >
            {session.transactionSucceeded
              ? "Done"
              : `Continue To ${depositMode ? "Deposit" : "Withdraw"}`}
          </button>
        </Col>
      </Row>
    </div>
  );

  const changeLimit = (
    <div className="limit-change">
      <Row className="limit-change-title">
        <Col>Request Limit Change</Col>
      </Row>
      <Row className="limit-description">
        <Col>
          Please specify your desired VIP Preferred limit. Your request must be
          in an increment of $5, and can be any amount between $5 and $
          {formattedMaxLimitAmount}.
        </Col>
      </Row>
      <Row>
        <Col className={isMobile ? "col-10 offset-1" : "col-md-6 offset-1"}>
          <FormField
            prepend="$"
            fieldName="limitAmount"
            label="CHANGE MY LIMIT TO:"
            maxLength="20"
            placeholder={limitAmount?.toLocaleString()}
            required={true}
            validators={[NUMBERS_ONLY, INCREMENTS_OF_5]}
            onValueChanged={onFieldChange}
            autoComplete="off"
          />
        </Col>
        <Col className={isMobile ? "col-10 offset-1" : "col-md-4"}>
          <button
            disabled={!proceed}
            className={proceed ? "submit-button" : "submit-button disabled"}
            onClick={submitForm}
          >
            Submit
          </button>
        </Col>
      </Row>
    </div>
  );

  return (
    <>
      <div className="manage-limit">
        <div className="title text-center">Limit Change</div>
        <div className="limit">
          {successful ? "New" : "Current"} Available Limit:
        </div>
        <div className="amount">
          <span className="dollar-sign-cents">$</span>
          <span className="dollar">{limit}</span>
        </div>
        {loading && <Loading />}
        {!loading && !submitted && changeLimit}
        {!loading && !successful && submitted && error}
        {!loading && successful && submitted && success}
      </div>
    </>
  );
};

export default ManageLimit;
